
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { DataContext } from '../Provider/dataProvider';
import HealthLogForm from '../components/health/HealthLogForm';
import HealthCharts from '../components/health/HealthCharts';
import MembershipRequiredView from '../components/MembershipRequiredView';
import { fetchWithAuth } from '../utils/authUtils';
import { isMembershipAccessDenied, isMembershipDeniedError } from '../utils/membershipAccess';
import { FEATURES } from '../constants/featureAccess';
import { useElderlyFeatureAccess } from '../hooks/useFeatureAccess';
import '../css/healthMonitor.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const toSeriesDateKey = (value) => {
    if (!value) return '';
    const raw = String(value);
    const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return toLocalDateString(date);
};

const formatValue = (value, digits = 1) => {
    if (value === null || value === undefined || value === '') return '—';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toLocaleString('vi-VN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Chưa có dữ liệu';
    const date = new Date(dateValue);
    // Adjust to Vietnam timezone (UTC+7)
    const vietnamTime = new Date(date.getTime() + (7 * 60 * 60 * 1000));
    const day = String(vietnamTime.getUTCDate()).padStart(2, '0');
    const month = String(vietnamTime.getUTCMonth() + 1).padStart(2, '0');
    const year = vietnamTime.getUTCFullYear();
    const hours = String(vietnamTime.getUTCHours()).padStart(2, '0');
    const minutes = String(vietnamTime.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes} ${day}/${month}/${year}`;
};

const HealthMonitor = () => {
    const { elderlyId: paramElderlyId } = useParams();
    const { data } = useContext(DataContext);
    const [range, setRange] = useState('7days');
    const [series, setSeries] = useState([]);
    const [latest, setLatest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [aiWarnings, setAiWarnings] = useState([]);
    const [warningsLoading, setWarningsLoading] = useState(false);
    const [warningsError, setWarningsError] = useState('');
    const [showRiskCard, setShowRiskCard] = useState(true);

    const [linkedElderlies, setLinkedElderlies] = useState([]);
    const [selectedElderly, setSelectedElderly] = useState('');
    const [loadingElderlies, setLoadingElderlies] = useState(false);

    // Aggregated summary state (daily/weekly/monthly)
    const [aggMode, setAggMode] = useState('daily'); // 'daily' | 'weekly' | 'monthly'
    const [rangeAgg, setRangeAgg] = useState('7days'); // reuse 7/30 days window
    const [seriesAgg, setSeriesAgg] = useState([]);
    const [loadingAgg, setLoadingAgg] = useState(false);
    const [errorAgg, setErrorAgg] = useState('');
    const [membershipDenied, setMembershipDenied] = useState(false);

    const isRelative = data?.role === 'relative';
    const noParamElderlyId = !paramElderlyId;
    const resolvedElderlyId = useMemo(() => {
        if (paramElderlyId) return paramElderlyId;
        if (isRelative && noParamElderlyId && selectedElderly) return selectedElderly;
        if (isRelative && noParamElderlyId) return '';
        return data?.profileId || data?.id || '';
    }, [paramElderlyId, data, isRelative, noParamElderlyId, selectedElderly]);

    const { canUse: canUseForElderly, loading: accessLoading } = useElderlyFeatureAccess(resolvedElderlyId);
    const membershipLocked = Boolean(
        resolvedElderlyId
        && data?.role !== 'admin'
        && !accessLoading
        && (!canUseForElderly(FEATURES.HEALTH_MONITOR) || membershipDenied)
    );

    const renderDataError = (message) => {
        if (membershipDenied || isMembershipDeniedError({ message })) {
            return (
                <div className="health-card">
                    <MembershipRequiredView compact />
                </div>
            );
        }
        return <div className="health-card health-error-state">{message}</div>;
    };
    const loadAiEngine = async () => {
        if (!resolvedElderlyId) return;
        try {
            setWarningsLoading(true);
            setWarningsError('');

            const response = await fetchWithAuth(`${API_BASE}/api/ai/engine?elderlyId=${resolvedElderlyId}`);
            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(payload?.message || 'Không thể tải dữ liệu AI');
            }

            setAiWarnings(payload?.data ? [payload.data] : []);
        } catch (err) {
            setAiWarnings([]);
            setWarningsError(err?.message || 'Lỗi khi tải dữ liệu AI');
        } finally {
            setWarningsLoading(false);
        }
    };

    const computeAggRange = () => {
        const days = rangeAgg === '30days' ? 30 : 7;
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - days + 1);
        return {
            from: toLocalDateString(start),
            to: toLocalDateString(end)
        };
    };

    const loadSummaryAgg = async () => {
        if (!resolvedElderlyId) return;
        try {
            setLoadingAgg(true);
            setErrorAgg('');
            const { from, to } = computeAggRange();
            const path = aggMode === 'weekly' ? 'weekly' : aggMode === 'monthly' ? 'monthly' : 'daily';
            const url = `${API_BASE}/api/health/summary/${path}?elderlyId=${resolvedElderlyId}&from=${from}&to=${to}`;
            const res = await fetchWithAuth(url);
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json?.success) {
                if (isMembershipAccessDenied(res.status, json)) {
                    setMembershipDenied(true);
                    setErrorAgg('');
                    return;
                }
                throw new Error(json?.message || 'Không thể tải báo cáo.');
            }

            const rows = Array.isArray(json.data) ? json.data : [];
            const mapped = rows
                .slice()
                .reverse()
                .map((r) => ({
                    date: toSeriesDateKey(r.summaryDate || r.periodStart || r.date || ''),
                    systolic: r.avgSystolic != null ? Number(r.avgSystolic) : null,
                    diastolic: r.avgDiastolic != null ? Number(r.avgDiastolic) : null,
                    heartRate: r.avgHeartRate != null ? Number(r.avgHeartRate) : null,
                    bloodSugar: r.avgBloodSugar != null ? Number(r.avgBloodSugar) : null,
                    weight: r.avgWeight != null ? Number(r.avgWeight) : null
                }));
            setSeriesAgg(mapped);
        } catch (err) {
            setSeriesAgg([]);
            setErrorAgg(err?.message || 'Lỗi khi tải báo cáo.');
        } finally {
            setLoadingAgg(false);
        }
    };

    const loadHealthData = async () => {
        if (!resolvedElderlyId) {

            return;
        }

        try {

            setLoading(true);
            setError('');
            setMembershipDenied(false);
            

            
            const response = await fetchWithAuth(
                `${API_BASE}/api/health-logs/charts/${resolvedElderlyId}?range=${range}`
            );

            const payload = await response.json().catch(() => ({}));
            

            if (!response.ok) {
                if (isMembershipAccessDenied(response.status, payload)) {
                    setMembershipDenied(true);
                    setError('');
                    return;
                }
                throw new Error(payload?.message || 'Không thể tải dữ liệu sức khỏe. Mã lỗi: ' + response.status);
            }

            const nextSeries = Array.isArray(payload?.data?.series) ? payload.data.series : [];

            setSeries(nextSeries);
            const nextLatest = payload?.data?.latest || null;
            setLatest(nextLatest);
            // do not auto-fetch warnings here (warnings fetched separately)

        } catch (err) {
            setSeries([]);
            setLatest(null);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch danh sách elderly phụ thuộc cho relative
    useEffect(() => {
        if (!isRelative || !noParamElderlyId) return;

        const fetchLinkedElderlies = async () => {
            try {
                setLoadingElderlies(true);
                const response = await fetchWithAuth(`${API_BASE}/relatives`);

                const payload = await response.json().catch(() => ({}));
                if (!response.ok || !payload?.success) {
                    setLinkedElderlies([]);
                    return;
                }

                const elderlies = Array.isArray(payload?.data) ? payload.data : [];
                setLinkedElderlies(elderlies);
                if (elderlies.length > 0) {
                    setSelectedElderly(String(elderlies[0].id));
                }
            } catch (err) {
                setLinkedElderlies([]);
            } finally {
                setLoadingElderlies(false);
            }
        };

        fetchLinkedElderlies();
    }, [isRelative, noParamElderlyId]);

    useEffect(() => {
        // Load data + then trigger risk analysis
        loadHealthData();

        const handleVisibility = () => {
            if (!document.hidden) {
                loadHealthData();
            }
        };

        window.addEventListener('focus', loadHealthData);
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            window.removeEventListener('focus', loadHealthData);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedElderlyId, range]);

    // Fetch AI engine data once when we first have a resolvedElderlyId
    useEffect(() => {
        if (!resolvedElderlyId) return;
        loadAiEngine();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedElderlyId]);

    // Load aggregated summary when elderly/range/mode changes
    useEffect(() => {
        loadSummaryAgg();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedElderlyId, aggMode, rangeAgg]);

    // after save handler -> refresh charts and then AI engine
    const handleAfterSave = async () => {
        await loadHealthData();
        await loadSummaryAgg();
        await loadAiEngine();
        window.setTimeout(() => loadAiEngine(), 3000);
        window.setTimeout(() => loadAiEngine(), 8000);
    };

    const latestWarning = aiWarnings?.length > 0 ? aiWarnings[0] : null;
    const warningTitle = latestWarning
        ? `AI Phân tích nguy cơ (Cập nhật: ${formatDateTime(latestWarning.created_at)})`
        : 'AI Phân tích nguy cơ';
    const warningClass = `ai-warning-card ${latestWarning?.level || 'low'}`;

    const summaryCards = [
        { label: 'Huyết áp trên (mmHg)', value: latest?.systolic, color: '#ef4444', hint: 'Bình thường: 90–120 mmHg' },
        { label: 'Huyết áp dưới (mmHg)', value: latest?.diastolic, color: '#f59e0b', hint: 'Bình thường: 60–80 mmHg' },
        { label: 'Nhịp tim (bpm)', value: latest?.heartRate, color: '#2563eb', hint: 'Nghỉ ngơi: 60–100 bpm' },
        { label: 'Đường huyết (mmol/L)', value: latest?.bloodSugar, color: '#16a34a', hint: 'Đói: 4.0–7.0 mmol/L' },
        { label: 'Nhiệt độ (°C)', value: latest?.temperature, color: '#0f766e', hint: 'Bình thường: 36.5–37.5°C' },
        { label: 'Cân nặng (kg)', value: latest?.weight, color: '#7c3aed', hint: 'Đơn vị: kilogram (kg)' }
    ];

    return (
        <div className="health-monitor-page">
            <div className="health-monitor-shell">
                <div className="health-monitor-topbar">
                    <div>
                        
                        <h1>Chỉ số sức khỏe</h1>
                        <p className="health-subtitle">
                            {isRelative
                                ? 'Người thân có thể theo dõi và cập nhật chỉ số sinh học gần nhất của cụ.'
                                : 'Bác có thể theo dõi chỉ số sức khỏe và xem xu hướng theo ngày/tuần/tháng.'}
                        </p>
                    </div>
                </div>

                {isRelative && noParamElderlyId ? (
                    // Relative ở /health: hiển thị dropdown chọn elderly
                    <>
                        {loadingElderlies ? (
                            <div className="health-card health-loading-state">Đang tải danh sách người cao tuổi...</div>
                        ) : linkedElderlies.length === 0 ? (
                            <div className="health-card health-empty-state">
                                <h2>Chưa có người cao tuổi phụ thuộc</h2>
                                <p>Hãy kết nối với người cao tuổi để cập nhật chỉ số sức khỏe.</p>
                            </div>
                        ) : (
                            <>
                                <section className="health-card health-elderly-selector">
                                    <div className="health-card-header">
                                        <div>
                                            <p className="health-eyebrow">Chọn người cao tuổi</p>
                                            <h2>Vui lòng chọn cụ để cập nhật chỉ số sức khỏe</h2>
                                        </div>
                                    </div>
                                    <div className="health-elderly-selector-wrapper">
                                        <label className="health-elderly-selector-label">Người cao tuổi phụ thuộc:</label>
                                        <select
                                            value={selectedElderly}
                                            onChange={(e) => setSelectedElderly(e.target.value)}
                                            className="health-elderly-select"
                                        >
                                            <option value="">-- Chọn người cao tuổi --</option>
                                            {linkedElderlies.map((elderly) => (
                                                <option key={elderly.id} value={String(elderly.id)}>
                                                    {elderly.full_name || elderly.fullName || `Cụ ${elderly.id}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </section>

                        {selectedElderly && (
                            membershipLocked ? (
                                <div className="health-card">
                                    <MembershipRequiredView compact />
                                </div>
                            ) : (
                            <>
                                <section className="health-card health-summary-card">
                                    <div className="health-card-header">
                                        <div>
                                            <p className="health-eyebrow">Chỉ số gần nhất</p>
                                            <h2>{latest ? `Đã cập nhật lúc ${formatDateTime(latest.measuredAt)}` : 'Chưa có dữ liệu gần nhất'}</h2>
                                        </div>
                                    </div>

                                    <div className="health-summary-grid">
                                        {summaryCards.map((item) => (
                                            <div key={item.label} className="health-summary-item" style={{ borderColor: item.color }}>
                                                <span title={item.hint || ''}>{item.label}</span>
                                                <strong style={{ color: item.color }}>{formatValue(item.value)}</strong>
                                                {item.hint && (
                                                    <div className="health-summary-hint">{item.hint}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                <section className="health-card health-risk-card">
                                    <div className="health-card-header">
                                        <div>
                                            <p className="health-eyebrow">Phân tích AI</p>
                                            <h2>{warningsLoading ? 'Đang tải...' : warningTitle}</h2>
                                        </div>
                                        <button
                                            type="button"
                                            className="health-toggle-card-button"
                                            onClick={() => setShowRiskCard((prev) => !prev)}
                                        >
                                            {showRiskCard ? 'Ẩn' : 'Hiện'}
                                        </button>
                                    </div>

                                    {showRiskCard && (
                                        warningsError ? (
                                            <div className="health-risk-error">{warningsError}</div>
                                        ) : latestWarning ? (
                                            <div className={warningClass}>
                                                <div className="ai-warning-section">
                                                    <h3>Phân tích nguy cơ</h3>
                                                    <div className="ai-warning-text">{latestWarning.analysis_result}</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="health-risk-empty">Chưa có phân tích. Dữ liệu AI sẽ xuất hiện sau khi có dữ liệu xử lý.</div>
                                        )
                                    )}
                                </section>

                                <HealthLogForm elderlyId={selectedElderly} initialValues={latest} onSaved={handleAfterSave} />

                                        {loading ? (
                                            <div className="health-card health-loading-state">Đang tải dữ liệu sức khỏe...</div>
                                        ) : error ? (
                                            renderDataError(error)
                                        ) : series.length === 0 ? (
                                            <div className="health-card health-empty-chart">Chưa có dữ liệu để hiển thị biểu đồ.</div>
                                        ) : (
                                            <HealthCharts series={series} range={range} onRangeChange={setRange} />
                                        )}

                                        <section className="health-card health-chart-shell">
                                            <div className="health-card-header">
                                                <div>
                                                    <p className="health-eyebrow">Báo cáo tổng hợp</p>
                                                    <h2>Ngày / Tuần / Tháng</h2>
                                                </div>
                                                <div className="health-range-switcher" style={{ display: 'flex', gap: 8 }}>
                                                    <div className="btn-group">
                                                        <button type="button" className={`health-range-btn ${aggMode === 'daily' ? 'active' : ''}`} onClick={() => setAggMode('daily')}>Ngày</button>
                                                        <button type="button" className={`health-range-btn ${aggMode === 'weekly' ? 'active' : ''}`} onClick={() => setAggMode('weekly')}>Tuần</button>
                                                        <button type="button" className={`health-range-btn ${aggMode === 'monthly' ? 'active' : ''}`} onClick={() => setAggMode('monthly')}>Tháng</button>
                                                    </div>
                                                    <div className="btn-group">
                                                        <button type="button" className={`health-range-btn ${rangeAgg === '7days' ? 'active' : ''}`} onClick={() => setRangeAgg('7days')}>7 ngày</button>
                                                        <button type="button" className={`health-range-btn ${rangeAgg === '30days' ? 'active' : ''}`} onClick={() => setRangeAgg('30days')}>30 ngày</button>
                                                    </div>
                                                </div>
                                            </div>

                                            {loadingAgg ? (
                                                <div className="health-card health-loading-state">Đang tải báo cáo...</div>
                                            ) : errorAgg ? (
                                                renderDataError(errorAgg)
                                            ) : seriesAgg.length === 0 ? (
                                                <div className="health-card health-empty-chart">Chưa có dữ liệu để hiển thị biểu đồ.</div>
                                            ) : (
                                                <HealthCharts series={seriesAgg} range={rangeAgg} onRangeChange={setRangeAgg} />
                                            )}

                                            {(!loadingAgg && !errorAgg && seriesAgg.length > 0) && (
                                                <div className="health-card" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end' }}>
                                                    <button type="button" className="tamcare-btn-ghost" onClick={() => {
                                                        try {
                                                            const headers = ['date','systolic','diastolic','heartRate','bloodSugar'];
                                                            const rows = (seriesAgg || []).map(r => [r.date || '', r.systolic ?? '', r.diastolic ?? '', r.heartRate ?? '', r.bloodSugar ?? '']);
                                                            const csv = [headers.join(','), ...rows.map(c => c.join(','))].join('\n');
                                                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                                            const url = URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = url;
                                                            const { from, to } = computeAggRange();
                                                            a.download = `health-report_${resolvedElderlyId || 'me'}_${aggMode}_${from}_${to}.csv`;
                                                            a.click();
                                                            URL.revokeObjectURL(url);
                                                        } catch (_) {}
                                                    }}>
                                                        <i className="fa-solid fa-file-csv" style={{ marginRight: 6 }}></i>
                                                        Xuất CSV
                                                    </button>
                                                    <button type="button" className="tamcare-btn-primary" onClick={() => {
                                                        try {
                                                            const win = window.open('', '_blank');
                                                            if (!win) return;
                                                            const title = `Báo cáo sức khỏe (${aggMode})`;
                                                            const { from, to } = computeAggRange();
                                                            const tableRows = (seriesAgg || []).map(r => `
                                                                <tr>
                                                                  <td>${r.date || ''}</td>
                                                                  <td>${r.systolic ?? ''}</td>
                                                                  <td>${r.diastolic ?? ''}</td>
                                                                  <td>${r.heartRate ?? ''}</td>
                                                                  <td>${r.bloodSugar ?? ''}</td>
                                                                </tr>`).join('');
                                                            win.document.write(`
                                                                <html>
                                                                  <head>
                                                                    <meta charset="utf-8" />
                                                                    <title>${title}</title>
                                                                    <style>
                                                                      body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
                                                                      h1 { margin: 0 0 8px; font-size: 22px; color: #1e293b; }
                                                                      .subtitle { color: #64748b; margin-bottom: 16px; }
                                                                      table { width: 100%; border-collapse: collapse; }
                                                                      th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: center; }
                                                                      th { background: #f8fafc; }
                                                                    </style>
                                                                  </head>
                                                                  <body>
                                                                    <h1>${title}</h1>
                                                                    <div class="subtitle">Khoảng thời gian: ${from} → ${to} · Elderly: ${resolvedElderlyId || "me"} · Chế độ: ${aggMode}</div>
                                                                    <table>
                                                                      <thead>
                                                                        <tr>
                                                                          <th>Ngày/Bắt đầu kỳ</th>
                                                                          <th>Huyết áp trên (mmHg)</th>
                                                                          <th>Huyết áp dưới (mmHg)</th>
                                                                          <th>Nhịp tim (bpm)</th>
                                                                          <th>Đường huyết (mmol/L)</th>
                                                                        </tr>
                                                                      </thead>
                                                                      <tbody>
                                                                        ${tableRows}
                                                                      </tbody>
                                                                    </table>
                                                                    <script>window.print(); setTimeout(() => window.close(), 300);</script>
                                                                  </body>
                                                                </html>
                                                            `);
                                                            win.document.close();
                                                        } catch (_) {}
                                                    }}>
                                                        <i className="fa-solid fa-file-pdf" style={{ marginRight: 6 }}></i>
                                                        Xuất PDF
                                                    </button>
                                                </div>
                                            )}
                                        </section>
                            </>
                            )
                        )}
                            </>
                        )}
                    </>
                ) : !resolvedElderlyId ? (
                    <div className="health-card health-empty-state">
                        <h2>Chưa xác định được hồ sơ người cao tuổi</h2>
                        <p>Hãy mở màn hình này từ hồ sơ của cụ hoặc từ trang người thân đã kết nối.</p>
                    </div>
                ) : membershipLocked ? (
                    <div className="health-card">
                        <MembershipRequiredView />
                    </div>
                ) : (
                    <>
                        <section className="health-card health-summary-card">
                            <div className="health-card-header">
                                <div>
                                    <p className="health-eyebrow">Chỉ số gần nhất</p>
                                    <h2>{latest ? `Đã cập nhật lúc ${formatDateTime(latest.measuredAt)}` : 'Chưa có dữ liệu gần nhất'}</h2>
                                </div>
                            </div>

                            <div className="health-summary-grid">
                                {summaryCards.map((item) => (
                                    <div key={item.label} className="health-summary-item" style={{ borderColor: item.color }}>
                                        <span title={item.hint || ''}>{item.label}</span>
                                        <strong style={{ color: item.color }}>{formatValue(item.value)}</strong>
                                        {item.hint && (
                                            <div className="health-summary-hint">{item.hint}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        {isRelative ? (
                            <HealthLogForm elderlyId={resolvedElderlyId} initialValues={latest} onSaved={handleAfterSave} />
						) : null}

                        <section className="health-card health-risk-card">
                            <div className="health-card-header">
                                <div>
                                    <p className="health-eyebrow">Phân tích AI</p>
                                    <h2>{warningsLoading ? 'Đang tải...' : warningTitle}</h2>
                                </div>
                                <button
                                    type="button"
                                    className="health-toggle-card-button"
                                    onClick={() => setShowRiskCard((prev) => !prev)}
                                >
                                    {showRiskCard ? 'Ẩn' : 'Hiện'}
                                </button>
                            </div>

                            {showRiskCard && (
                                warningsError ? (
                                    <div className="health-risk-error">{warningsError}</div>
                                ) : latestWarning ? (
                                    <div className={warningClass}>
                                        <div className="ai-warning-section">
                                            <h3>Phân tích nguy cơ</h3>
                                            <div className="ai-warning-text">{latestWarning.analysis_result}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="health-risk-empty">Chưa có phân tích. Dữ liệu AI sẽ xuất hiện sau khi có dữ liệu xử lý.</div>
                                )
                            )}
                        </section>

                        {loading ? (
                            <div className="health-card health-loading-state">Đang tải dữ liệu sức khỏe...</div>
                        ) : error ? (
                            renderDataError(error)
                        ) : series.length === 0 ? (
                            <div className="health-card health-empty-chart">Chưa có dữ liệu để hiển thị biểu đồ.</div>
                        ) : (
                            <HealthCharts series={series} range={range} onRangeChange={setRange} />
                        )}

                        <section className="health-card health-chart-shell">
                            <div className="health-card-header">
                                <div>
                                    <p className="health-eyebrow">Báo cáo tổng hợp</p>
                                    <h2>Ngày / Tuần / Tháng</h2>
                                </div>
                                <div className="health-range-switcher" style={{ display: 'flex', gap: 8 }}>
                                    <div className="btn-group">
                                        <button type="button" className={`health-range-btn ${aggMode === 'daily' ? 'active' : ''}`} onClick={() => setAggMode('daily')}>Ngày</button>
                                        <button type="button" className={`health-range-btn ${aggMode === 'weekly' ? 'active' : ''}`} onClick={() => setAggMode('weekly')}>Tuần</button>
                                        <button type="button" className={`health-range-btn ${aggMode === 'monthly' ? 'active' : ''}`} onClick={() => setAggMode('monthly')}>Tháng</button>
                                    </div>
                                    <div className="btn-group">
                                        <button type="button" className={`health-range-btn ${rangeAgg === '7days' ? 'active' : ''}`} onClick={() => setRangeAgg('7days')}>7 ngày</button>
                                        <button type="button" className={`health-range-btn ${rangeAgg === '30days' ? 'active' : ''}`} onClick={() => setRangeAgg('30days')}>30 ngày</button>
                                    </div>
                                </div>
                            </div>

                            {loadingAgg ? (
                                <div className="health-card health-loading-state">Đang tải báo cáo...</div>
                            ) : errorAgg ? (
                                renderDataError(errorAgg)
                            ) : seriesAgg.length === 0 ? (
                                <div className="health-card health-empty-chart">Chưa có dữ liệu để hiển thị biểu đồ.</div>
                            ) : (
                                <HealthCharts series={seriesAgg} range={rangeAgg} onRangeChange={setRangeAgg} />
                            )}

                            {(!loadingAgg && !errorAgg && seriesAgg.length > 0) && (
                                <div className="health-card" style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <button type="button" className="tamcare-btn-ghost" onClick={() => {
                                        try {
                                            const headers = ['date','systolic','diastolic','heartRate','bloodSugar'];
                                            const rows = (seriesAgg || []).map(r => [r.date || '', r.systolic ?? '', r.diastolic ?? '', r.heartRate ?? '', r.bloodSugar ?? '']);
                                            const csv = [headers.join(','), ...rows.map(c => c.join(','))].join('\n');
                                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            const { from, to } = computeAggRange();
                                            a.download = `health-report_${resolvedElderlyId || 'me'}_${aggMode}_${from}_${to}.csv`;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        } catch (_) {}
                                    }}>
                                        <i className="fa-solid fa-file-csv" style={{ marginRight: 6 }}></i>
                                        Xuất CSV
                                    </button>
                                    <button type="button" className="tamcare-btn-primary" onClick={() => {
                                        try {
                                            const win = window.open('', '_blank');
                                            if (!win) return;
                                            const title = `Báo cáo sức khỏe (${aggMode})`;
                                            const { from, to } = computeAggRange();
                                            const tableRows = (seriesAgg || []).map(r => `
                                                <tr>
                                                  <td>${r.date || ''}</td>
                                                  <td>${r.systolic ?? ''}</td>
                                                  <td>${r.diastolic ?? ''}</td>
                                                  <td>${r.heartRate ?? ''}</td>
                                                  <td>${r.bloodSugar ?? ''}</td>
                                                </tr>`).join('');
                                            win.document.write(`
                                                <html>
                                                  <head>
                                                    <meta charset="utf-8" />
                                                    <title>${title}</title>
                                                    <style>
                                                      body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
                                                      h1 { margin: 0 0 8px; font-size: 22px; color: #1e293b; }
                                                      .subtitle { color: #64748b; margin-bottom: 16px; }
                                                      table { width: 100%; border-collapse: collapse; }
                                                      th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: center; }
                                                      th { background: #f8fafc; }
                                                    </style>
                                                  </head>
                                                  <body>
                                                    <h1>${title}</h1>
                                                    <div class="subtitle">Khoảng thời gian: ${from} → ${to} · Elderly: ${resolvedElderlyId || "me"} · Chế độ: ${aggMode}</div>
                                                    <table>
                                                      <thead>
                                                        <tr>
                                                          <th>Ngày/Bắt đầu kỳ</th>
                                                          <th>Huyết áp trên (mmHg)</th>
                                                          <th>Huyết áp dưới (mmHg)</th>
                                                          <th>Nhịp tim (bpm)</th>
                                                          <th>Đường huyết (mmol/L)</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        ${tableRows}
                                                      </tbody>
                                                    </table>
                                                    <script>window.print(); setTimeout(() => window.close(), 300);</script>
                                                  </body>
                                                </html>
                                            `);
                                            win.document.close();
                                        } catch (_) {}
                                    }}>
                                        <i className="fa-solid fa-file-pdf" style={{ marginRight: 6 }}></i>
                                        Xuất PDF
                                    </button>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export default HealthMonitor;
